(() => {
  const galleries = new WeakMap();
  class ProductGallery {
    constructor(root) {
      this.root = root;
      this.abort = new AbortController();
      this.mainImage = root.querySelector('[data-gallery-main-image]');
      this.thumbs = Array.from(root.querySelectorAll('[data-gallery-thumb]'));
      this.openButtons = Array.from(root.querySelectorAll('[data-gallery-open]'));
      this.lightbox = root.querySelector('[data-gallery-lightbox]');
      this.lightboxShell = root.querySelector('[data-gallery-lightbox-shell]');
      this.lightboxViewport = root.querySelector('[data-gallery-lightbox-viewport]');
      this.lightboxImage = root.querySelector('[data-gallery-lightbox-image]');
      this.closeButton = root.querySelector('[data-gallery-close]');
      this.rail = root.querySelector('[data-gallery-rail]');
      this.prevRailButton = root.querySelector('[data-gallery-rail-prev]');
      this.nextRailButton = root.querySelector('[data-gallery-rail-next]');
      this.canHoverZoom = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      this.handleRailScroll = this.handleRailScroll.bind(this);
      this.handleLightboxMove = this.handleLightboxMove.bind(this);
      this.handleLightboxLeave = this.handleLightboxLeave.bind(this);
      this.activeIndex = Math.max(
        this.thumbs.findIndex((thumb) => thumb.classList.contains('is-active')),
        0
      );

      if (!this.mainImage) {
        return;
      }

      this.bindEvents();

      if (this.thumbs.length > 0) {
        this.setActive(this.activeIndex, false);
      }
    }

    on(target, type, callback, options = {}) {
      target?.addEventListener(type, callback, { ...options, signal: this.abort.signal });
    }

    bindEvents() {
      this.thumbs.forEach((thumb, index) => {
        this.on(thumb, 'click', () => this.setActive(index));
      });

      if (this.rail) {
        this.on(this.rail, 'scroll', this.handleRailScroll, { passive: true });
        this.handleRailScroll();
      }

      if (this.prevRailButton) {
        this.on(this.prevRailButton, 'click', () => this.scrollRail(-1));
      }

      if (this.nextRailButton) {
        this.on(this.nextRailButton, 'click', () => this.scrollRail(1));
      }

      this.openButtons.forEach((button) => {
        this.on(button, 'click', () => this.openLightbox(button));
      });

      if (this.lightbox) {
        this.on(this.lightbox, 'click', (event) => {
          if (event.target === this.lightbox) {
            this.closeLightbox();
          }
        });
        this.on(this.lightbox, 'close', () => {
          this.resetZoom();
          document.body.classList.remove('is-gallery-open');
          if (this.lightboxTrigger?.isConnected) {
            this.lightboxTrigger.focus({ preventScroll: true });
          }
        });
      }

      if (this.closeButton) {
        this.on(this.closeButton, 'click', () => this.closeLightbox());
      }

      if (this.lightboxViewport) {
        this.on(this.lightboxViewport, 'mousemove', this.handleLightboxMove);
        this.on(this.lightboxViewport, 'mouseleave', this.handleLightboxLeave);
      }
    }

    getActiveThumb() {
      return this.thumbs[this.activeIndex] || null;
    }

    scrollRail(direction) {
      if (!this.rail) {
        return;
      }

      const amount = Math.max(this.rail.clientWidth * 0.7, 180);
      this.rail.scrollBy({
        left: direction * amount,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    }

    handleRailScroll() {
      if (!this.rail) {
        return;
      }

      const maxScrollLeft = this.rail.scrollWidth - this.rail.clientWidth - 2;
      const atStart = this.rail.scrollLeft <= 2;
      const atEnd = this.rail.scrollLeft >= maxScrollLeft;

      this.root.classList.toggle('is-rail-start', atStart);
      this.root.classList.toggle('is-rail-end', atEnd);
    }

    setActive(index, scrollRail = true) {
      if (this.thumbs.length === 0) {
        return;
      }

      const nextIndex = (index + this.thumbs.length) % this.thumbs.length;
      const activeThumb = this.thumbs[nextIndex];

      if (!activeThumb) {
        return;
      }

      this.activeIndex = nextIndex;

      const { mainSrc, zoomSrc, alt, width, height } = activeThumb.dataset;
      this.mainImage.src = mainSrc;
      this.mainImage.alt = alt || this.mainImage.alt;
      this.mainImage.dataset.zoomSrc = zoomSrc || mainSrc;

      if (width) {
        this.mainImage.width = Number(width);
      }

      if (height) {
        this.mainImage.height = Number(height);
      }

      this.thumbs.forEach((thumb, thumbIndex) => {
        const isActive = thumbIndex === nextIndex;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-pressed', String(isActive));
      });

      if (scrollRail && this.rail) {
        const thumbBounds = activeThumb.getBoundingClientRect();
        const railBounds = this.rail.getBoundingClientRect();
        const left = this.rail.scrollLeft + thumbBounds.left - railBounds.left
          - (this.rail.clientWidth - thumbBounds.width) / 2;
        this.rail.scrollTo({
          left: Math.max(0, left),
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
        });
      }

      this.updateLightboxImage();
    }

    updateLightboxImage() {
      if (!this.lightboxImage) {
        return;
      }

      const activeThumb = this.getActiveThumb();

      if (activeThumb) {
        const { mainSrc, zoomSrc, alt, width, height } = activeThumb.dataset;
        this.lightboxImage.src = zoomSrc || mainSrc;
        this.lightboxImage.alt = alt || this.lightboxImage.alt;

        if (width) {
          this.lightboxImage.width = Number(width);
        }

        if (height) {
          this.lightboxImage.height = Number(height);
        }

        return;
      }

      this.lightboxImage.src = this.mainImage.dataset.zoomSrc || this.mainImage.currentSrc || this.mainImage.src;
      this.lightboxImage.alt = this.mainImage.alt;
      this.lightboxImage.width = this.mainImage.width;
      this.lightboxImage.height = this.mainImage.height;
    }

    openLightbox(trigger) {
      if (!this.lightbox || this.lightbox.open) {
        return;
      }

      this.lightboxTrigger = trigger || document.activeElement;
      this.updateLightboxImage();
      this.resetZoom();
      this.lightbox.showModal();
      document.body.classList.add('is-gallery-open');
    }

    closeLightbox() {
      if (!this.lightbox?.open) {
        return;
      }

      this.lightbox.close();
    }

    handleLightboxMove(event) {
      if (!this.canHoverZoom || !this.lightboxViewport || !this.lightboxImage || !this.lightbox?.open) {
        return;
      }

      const rect = this.lightboxViewport.getBoundingClientRect();

      if (!rect.width || !rect.height) {
        return;
      }

      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      const clampedX = Math.max(0, Math.min(100, x));
      const clampedY = Math.max(0, Math.min(100, y));

      this.lightboxViewport.style.setProperty('--zoom-x', `${clampedX}%`);
      this.lightboxViewport.style.setProperty('--zoom-y', `${clampedY}%`);
      this.lightboxViewport.classList.add('is-zoom-active');
    }

    handleLightboxLeave() {
      this.resetZoom();
    }

    resetZoom() {
      if (!this.lightboxViewport) {
        return;
      }

      this.lightboxViewport.classList.remove('is-zoom-active');
      this.lightboxViewport.style.removeProperty('--zoom-x');
      this.lightboxViewport.style.removeProperty('--zoom-y');
    }

    destroy() {
      this.abort.abort();
      if (this.lightbox?.open) this.lightbox.close();
      this.resetZoom();
      document.body.classList.remove('is-gallery-open');
    }
  }

  const galleryRoots = (scope) => scope.matches?.('[data-product-gallery]')
    ? [scope] : scope.querySelectorAll('[data-product-gallery]');
  const initialize = (scope = document) => {
    galleryRoots(scope).forEach((root) => {
      if (!galleries.has(root)) galleries.set(root, new ProductGallery(root));
    });
  };
  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    galleryRoots(event.target).forEach((root) => {
      galleries.get(root)?.destroy();
      galleries.delete(root);
    });
  });
})();
